import { memo, useMemo } from 'react'
import type { AudioRemixSourceOption, AudioSegmentGroup } from './audioRemixTypes'
import { getCharacterColor } from './audioRemixColors'
import { getSelectedTrackIndex } from './audioRemixSelection'

interface MixerVideoStripProps {
  videoRef: React.RefObject<HTMLVideoElement>
  origVideoUrl: string | null
  videoLoaded: boolean
  onVideoLoaded: () => void
  onVideoError: () => void
  playheadTime: number
  groups: AudioSegmentGroup[]
  sourceOptions: AudioRemixSourceOption[]
  selectedSourceIndex: number | null
  onSourceOriginalChange: (index: number) => void
  duckDepthDb: number | null
  onDuckDepthDbChange: (value: number | null) => void
}

export const MixerVideoStrip = memo(function MixerVideoStrip({
  videoRef,
  origVideoUrl,
  videoLoaded,
  onVideoLoaded,
  onVideoError,
  playheadTime,
  groups,
  sourceOptions,
  selectedSourceIndex,
  onSourceOriginalChange,
  duckDepthDb,
  onDuckDepthDbChange,
}: MixerVideoStripProps) {
  const currentSpeaker = useMemo(() => {
    for (const g of groups) {
      const idx = getSelectedTrackIndex(g.tracks)
      if (idx < 0) continue
      const track = g.tracks[idx]
      if (playheadTime >= track.startS && playheadTime < track.endS) {
        return { character: g.character, emotion: g.emotion, text: g.text, color: getCharacterColor(g.character, g.type) }
      }
    }
    return null
  }, [groups, playheadTime])

  const duckSliderValue = duckDepthDb === null ? 1.0 : duckDepthDb === -6 ? 0.33 : duckDepthDb === -12 ? 0.5 : duckDepthDb === -18 ? 0.67 : 0
  const handleDuckChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const v = parseFloat(e.target.value)
    if (v >= 0.9) onDuckDepthDbChange(null)
    else if (v >= 0.6) onDuckDepthDbChange(-18)
    else if (v >= 0.4) onDuckDepthDbChange(-12)
    else if (v >= 0.2) onDuckDepthDbChange(-6)
    else onDuckDepthDbChange(0)
  }

  return (
    <div className="h-[110px] bg-black border-b border-border flex items-center px-4 gap-4 shrink-0">
      {/* Video preview */}
      <div className="w-[168px] h-[94px] rounded bg-surface-0 border border-border overflow-hidden flex-shrink-0 relative">
        {origVideoUrl && (
          <video
            ref={videoRef}
            src={origVideoUrl}
            controls={false}
            className="w-full h-full object-cover"
            onLoadedData={onVideoLoaded}
            onCanPlay={onVideoLoaded}
            onError={onVideoError}
          />
        )}
        {!videoLoaded && (
          <div className="absolute inset-0 flex items-center justify-center">
            <div className="w-5 h-5 border-2 border-white/20 border-t-white/60 rounded-full animate-spin" />
          </div>
        )}
      </div>

      {/* Currently speaking character */}
      <div className="flex-1 flex flex-col gap-1.5 min-w-0">
        {sourceOptions.length > 0 && (
          <label className="flex items-center gap-2 text-xxs text-text-muted">
            <span className="shrink-0 uppercase tracking-wide">Base video</span>
            <select
              value={selectedSourceIndex ?? ''}
              onChange={(event) => onSourceOriginalChange(Number(event.target.value))}
              className="h-7 min-w-28 rounded-lg border border-input-border bg-input-bg px-2 text-xs text-foreground outline-none transition-colors hover:border-input-border-hover focus:border-primary focus:ring-2 focus:ring-primary/20"
              data-testid="audio-remix-source-original-select"
            >
              {sourceOptions.map((option) => (
                <option key={option.index} value={option.index}>
                  {option.label}
                </option>
              ))}
            </select>
          </label>
        )}
        {currentSpeaker ? (
          <>
            <div className="flex items-center gap-2">
              <span className="w-2 h-2 rounded-full flex-shrink-0" style={{ background: currentSpeaker.color.fg }} />
              <span className="text-[13px] font-semibold" style={{ color: currentSpeaker.color.fg }}>
                {currentSpeaker.character}
              </span>
              {currentSpeaker.emotion && (
                <span className="text-[11px] text-text-muted italic">({currentSpeaker.emotion})</span>
              )}
            </div>
            <div className="text-[12px] text-text-secondary italic truncate max-w-[460px]">
              &ldquo;{currentSpeaker.text}&rdquo;
            </div>
          </>
        ) : (
          <div className="text-[12px] text-text-muted">No dialogue at playhead</div>
        )}
        <div className="flex items-center gap-3.5 text-[10px] text-text-muted mt-0.5">
          <span>{groups.length} tracks</span>
          <span>{groups.filter(g => g.type === 'voiceover').length} voiceover</span>
        </div>
      </div>

      {/* Ducking control */}
      <div className="w-[210px] flex-shrink-0 p-2.5 bg-surface-1 border border-border rounded-md">
        <div className="text-[10px] text-text-muted uppercase tracking-wide mb-1.5">Dialogue ducking</div>
        <input
          type="range"
          min="0" max="1" step="0.01"
          value={duckSliderValue}
          onChange={handleDuckChange}
          className="w-full h-1 bg-surface-2 rounded appearance-none cursor-pointer [&::-webkit-slider-thumb]:appearance-none [&::-webkit-slider-thumb]:w-3 [&::-webkit-slider-thumb]:h-3 [&::-webkit-slider-thumb]:rounded-full [&::-webkit-slider-thumb]:bg-foreground"
        />
        <div className="flex justify-between text-[9px] text-text-muted/50 mt-1">
          <span>off</span><span>-12 dB</span><span>mute</span>
        </div>
      </div>
    </div>
  )
})
